import { assert } from 'assert-ts';
import { useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Checkbox } from '#components/atoms/forms/checkbox/Checkbox.tsx';
import styles from './PermissionGroup.module.css';

type Permission = {
  name: string;
  label?: string;
  description?: string;
};

type Props = {
  /** The label of the top-level checkbox.  Only used if there are > 1 permission */
  groupLabel: string;
  /** A description for the top-level checkbox */
  groupDescription?: string;
  /** An array of Permission objects, these are the form values */
  permissions: Permission[];
  /** Returns true for permissions that can't currently be granted (e.g. not allowed for a role-scoped key) */
  isPermissionDisabled?: (name: string) => boolean;
};

/**
 * A related grouping of permissions.  If the top-level row is checked, all permissions should be checked.
 * If it is unchecked, all permissions below are unchecked.  Checking all of the permissions individually
 * causes the top-level to become checked.  Disabled permissions are ignored by the rollup.
 */
export function PermissionGroup({ groupLabel, groupDescription, permissions, isPermissionDisabled }: Props) {
  const { register, setValue } = useFormContext(); // retrieve all hook methods
  const values = useWatch();
  const isDisabled = useCallback((name: string) => isPermissionDisabled?.(name) ?? false, [isPermissionDisabled]);
  const enabledPermissionNames = permissions.map((p) => p.name).filter((name) => !isDisabled(name));

  // Throw an error if there are no permissions provided
  const {
    label: firstLabel,
    name: firstName,
    description: firstDescription,
  } = assert(permissions[0], 'Must include at least one permission');

  /** Are all of the enabled permissions in this group checked? */
  const allChecked = useCallback(
    (v: { [name: string]: boolean }) => {
      // We only care about enabled permissions in this group, not the whole form
      return enabledPermissionNames.every((name) => v[name] === true);
    },
    [enabledPermissionNames]
  );

  /** Derive groupChecked state from current values */
  const groupChecked = enabledPermissionNames.length > 0 && allChecked(values);

  /** Either check or uncheck all of the enabled permissions of this group */
  function handleGroupCheck() {
    enabledPermissionNames.forEach((name) => {
      setValue(name, !groupChecked, { shouldDirty: true });
    });
  }

  // If there's only one permission, don't add a group rollup, just use the single permission
  if (permissions.length === 1) {
    return (
      <div className={styles.PermissionGroup_rollup}>
        <Checkbox label={firstLabel ?? firstName} {...register(firstName, { disabled: isDisabled(firstName) })} />
        <div className="p-sm">{firstDescription}</div>
      </div>
    );
  }

  // Otherwise, show a rollup and the permission list
  return (
    <div>
      <div className={styles.PermissionGroup_rollup}>
        <Checkbox
          label={groupLabel}
          checked={groupChecked}
          onChange={handleGroupCheck}
          disabled={enabledPermissionNames.length === 0}
        />
        <div className="p-sm">{groupDescription}</div>
      </div>
      {permissions.map(({ name, label, description }) => (
        <div className={styles.PermissionGroup_permission} key={name}>
          <Checkbox label={label ?? name} {...register(name, { disabled: isDisabled(name) })} />
          <div className="p-sm">{description}</div>
        </div>
      ))}
    </div>
  );
}
